// src/components/quizzes/BonusTriangles.js
//
// Triangles bonus round: catch the kind of triangle shown.
// Runs on the shared bonus-round engine (kit/BonusShell.js) — the
// BonusDiamonds look, bombs, bonus stars, clocks, combos, a new question
// every few right answers, and 🏁 End game. Same contract as before:
// BonusRouter renders <ThisGame onBonusComplete questions placeholderImage />.

import React from "react";
import BonusShell from "./kit/BonusShell";

// cute pastel fill palette

const SPAWN_MS = 480;
const MIN_TARGETS_ONSCREEN = 2;
const TARGET_SPEED_SLOWDOWN = 0.92;

/** ======= TRIANGLE TYPES ======= */
const TRI_TYPES = [
  "Acute Triangle",
  "Obtuse Triangle",
  "Right Triangle",
  "Equilateral Triangle",
  "Isosceles Triangle",
  "Scalene Triangle",
];

/** ======= UTIL ======= */
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

/** ======= TRIANGLE SHAPES (SVG points) =======
 * All shapes fit in 100x100 viewBox.
 * Each template guarantees the requested property.
 */
const TRI_SHAPES = {
  "Equilateral Triangle": [
    // equilateral centered-ish
    "50,8 92,86 8,86",
  ],
  "Isosceles Triangle": [
    // two equal sides (apex centered)
    "50,10 90,90 10,90",
    "50,15 85,85 15,85",
  ],
  "Scalene Triangle": [
    // all sides different
    "12,88 90,78 34,12",
    "18,84 92,60 30,18",
  ],
  "Right Triangle": [
    // right angle at bottom-left
    "10,90 90,90 10,20",
    // right angle at top-left
    "10,10 90,10 10,90",
  ],
  "Acute Triangle": [
    // all angles < 90
    "20,85 80,85 50,20",
    "18,82 86,74 44,18",
  ],
  "Obtuse Triangle": [
    // one angle > 90
    "12,86 92,86 30,24",
    "15,88 88,78 35,22",
  ],
};

/** Keep balanced mix and ensure some correct ones are present */
const shouldSpawnCorrectBalanced = (targetType, items) => {
  const correct = items.filter((it) => it.type === targetType).length;
  const wrong = items.length - correct;
  if (correct < MIN_TARGETS_ONSCREEN) return true;
  if (correct < wrong) return true;
  if (wrong < correct) return false;
  return Math.random() < 0.5;
};

const newTarget = (prev) => {
  let t = pick(TRI_TYPES);
  for (let i = 0; i < 8 && prev && t === prev.target; i += 1) t = pick(TRI_TYPES);
  return { target: t };
};
const TriangleArt = ({ points }) => (
  <svg viewBox="0 0 100 100" width="78%" height="78%" aria-hidden="true">
    <polygon points={points} fill="#1b1035" stroke="#fff" strokeWidth="5" strokeLinejoin="round" />
  </svg>
);

const TrianglesBonus = (props) => (
  <BonusShell
    {...props}
    title="Triangle Catch!"
    emoji="🔺"
    sub="Catch the kind of triangle shown at the top."
    intro="Look at the kind of triangle at the top and catch every one that matches. Each one is worth five points. Watch out for bombs! Press Start when you are ready."
    rules={[
      { icon: "✅", text: "The right triangle", pts: "+5" },
      { icon: "❌", text: "A different triangle", pts: "-3" },
    ]}
    init={newTarget}
    prompt={(s) => ({ big: s.target, small: "Catch every one you see!", say: `Catch the ${s.target}.` })}
    spawn={(s, onScreen) => {
      const want = shouldSpawnCorrectBalanced(s.target, onScreen.map((x) => x.value));
      const type = want ? s.target : pick(TRI_TYPES.filter((t) => t !== s.target));
      return { type, points: pick(TRI_SHAPES[type]) };
    }}
    check={(s, v) => v.type === s.target}
    label={(v) => <TriangleArt points={v.points} />}
    badPoints={3}
    itemSize={104}
    newEvery={3}
    spawnMs={SPAWN_MS}
    slowGood={TARGET_SPEED_SLOWDOWN}
  />
);

export default TrianglesBonus;
